import React, { useEffect, useRef, useState } from "react";
import { Card, CardHeader, CardBody, Col, Container, Row } from "reactstrap";
import * as FlexmonsterReact from "react-flexmonster";
import { reportsApi } from "../../../services/reportsServices";
import Loader from "../../../components/Loader";
import InformationModal from "../../../components/InformationModal";
import { utils } from "../../../utils/utils";

const DATE_FIELDS_TO_FORMAT = [
  "Due Date",
  "Est. Ship Date",
  "Waiting for Detailing",
  "Detailing",
  "Waiting for Check",
  "Checking",
  "Waiting for Scrub",
  "Scrubbing",
  "Waiting for Lookover",
  "Lookover",
  "Ready for Review",
  "Rev Generated",
];

// Map API column names to display names
const COLUMN_MAPPING = {
  jobNumber: "Job#",
  customerName: "Customer Name",
  sequenceDescription: "Sequence Description",
  estimatedShipDate: "Est. Ship Date",
  dueDate: "Due Date",
  status: "Current Status",
  jobStatusName: "Job Status",
  detailer: "Detailer",
  checker: "Checker",
  waitingForDetailingDate: "Waiting for Detailing",
  detailingDate: "Detailing",
  waitingForCheckDate: "Waiting for Check",
  checkingDate: "Checking",
  waitingForScrubDate: "Waiting for Scrub",
  scrubbingDate: "Scrubbing",
  waitingForLookoverDate: "Waiting for Lookover",
  lookoverDate: "Lookover",
  readyForReviewDate: "Ready for Review",
  revGeneratedDate: "Rev Generated",
};

// Flexible version of formatDatesToObjectsInArray that handles null values
const formatDatesFlexible = (data, fields) => {
  return data.map((item) => {
    const formattedItem = { ...item };
    fields.forEach((field) => {
      // Only try to format if it's not null, undefined, empty string, or "-"
      if (formattedItem[field] && formattedItem[field] !== "-") {
        try {
          // Use the existing utils.formatDate method - moment.js handles various formats
          formattedItem[field] = utils.formatDate(formattedItem[field]);
        } catch (error) {
          formattedItem[field] = "-";
        }
      }
    });
    return formattedItem;
  });
};

// Function to map API data to display columns
const mapApiDataToDisplayColumns = (apiData) => {
  return apiData.map((item) => {
    const mappedItem = {};
    Object.keys(item).forEach((apiKey) => {
      const displayKey = COLUMN_MAPPING[apiKey] || apiKey;
      mappedItem[displayKey] = item[apiKey] || "-";
    });
    return mappedItem;
  });
};

const columns = {
  "Job#": {
    type: "string",
  },
  "Customer Name": {
    type: "string",
  },
  "Sequence Description": {
    type: "string",
  },
  "Est. Ship Date": {
    type: "string",
  },
  "Due Date": {
    type: "string",
  },
  "Current Status": {
    type: "string",
  },
  "Job Status": {
    type: "string",
  },
  Detailer: {
    type: "string",
  },
  Checker: {
    type: "string",
  },
  "Waiting for Detailing": {
    type: "string",
  },
  Detailing: {
    type: "string",
  },
  "Waiting for Check": {
    type: "string",
  },
  Checking: {
    type: "string",
  },
  "Waiting for Scrub": {
    type: "string",
  },
  Scrubbing: {
    type: "string",
  },
  "Waiting for Lookover": {
    type: "string",
  },
  Lookover: {
    type: "string",
  },
  "Ready for Review": {
    type: "string",
  },
  "Rev Generated": {
    type: "string",
  },
};

const report = {
  dataSource: {
    data: [columns],
  },
  slice: {
    rows: [
      {
        uniqueName: "Job#",
      },
      {
        uniqueName: "Customer Name",
      },
      {
        uniqueName: "Sequence Description",
      },
      {
        uniqueName: "Est. Ship Date",
      },
      {
        uniqueName: "Due Date",
      },
      {
        uniqueName: "Current Status",
      },
      {
        uniqueName: "Job Status",
      },
      {
        uniqueName: "Detailer",
      },
      {
        uniqueName: "Checker",
      },
      {
        uniqueName: "Waiting for Detailing",
      },
      {
        uniqueName: "Detailing",
      },
      {
        uniqueName: "Waiting for Check",
      },
      {
        uniqueName: "Checking",
      },
      {
        uniqueName: "Waiting for Scrub",
      },
      {
        uniqueName: "Scrubbing",
      },
      {
        uniqueName: "Waiting for Lookover",
      },
      {
        uniqueName: "Lookover",
      },
      {
        uniqueName: "Ready for Review",
      },
      {
        uniqueName: "Rev Generated",
      },
    ],
    expands: {
      expandAll: true,
    },
  },
  options: {
    configuratorActive: false,
    grid: {
      type: "flat",
      showTotals: "off",
      showGrandTotals: "off",
      title: "Detailing Report",
    },
  },
};

const DetailingReport = () => {
  const pivot = useRef(null);
  const [loading, setLoading] = useState(false);
  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await reportsApi.detailingReport();
        // 1. Map API columns to display names
        const mappedResult = mapApiDataToDisplayColumns(result.data);
        // 2. Format dates with flexible version
        const formattedResult = formatDatesFlexible(
          mappedResult,
          DATE_FIELDS_TO_FORMAT,
        );

        if (pivot && pivot.current) {
          pivot.current.flexmonster.updateData({
            data: [columns, ...formattedResult],
          });
        }
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
        setInformationModal({
          isOpen: true,
          title: "Error",
          body: "There was an error loading the detailing report.",
        });
      }
    };

    fetchData();
  }, [pivot]);

  return (
    <Container fluid className="d-flex flex-column flex-grow-1">
      <Row className="flex-grow-1">
        <Col className="d-flex flex-column">
          <Card className="flex-grow-1">
            <CardHeader className="d-flex align-items-center justify-content-between">
              <h3 className="mb-0">Detailing Report</h3>
              <div className="d-flex align-items-center">
                {loading ? (
                  <div className="min-width-50">
                    <Loader size="sm" className="mr-3" />
                  </div>
                ) : null}
              </div>
            </CardHeader>
            <CardBody className="d-flex flex-column flex-grow-1">
              <div className="rounded border height-100">
                <FlexmonsterReact.Pivot
                  ref={pivot}
                  licenseKey={process.env.REACT_APP_FLEX_M_KEY}
                  toolbar={true}
                  height="100%"
                  report={report}
                  beforetoolbarcreated={(toolbar) =>
                    utils.customizeToolbarReport(
                      toolbar,
                      pivot,
                      "detailing_report",
                    )
                  }
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {informationModal.isOpen ? (
        <InformationModal
          {...informationModal}
          onClose={() => setInformationModal({ isOpen: false })}
        />
      ) : null}
    </Container>
  );
};

export default DetailingReport;
